import React, { useState } from 'react';
import { CreditCard, Smartphone, Check } from 'lucide-react';

interface MetodoPagamentoFormProps {
  onSelect: (metodo: 'PIX' | 'CREDIT_CARD') => void;
  onBack: () => void;
  metodoSelecionado?: 'PIX' | 'CREDIT_CARD';
  plano: {
    nome: string;
    preco: number;
  };
}

const MetodoPagamentoForm: React.FC<MetodoPagamentoFormProps> = ({ 
  onSelect, 
  onBack, 
  metodoSelecionado,
  plano 
}) => {
  const [loading, setLoading] = useState(false);

  const metodos = [
    {
      id: 'PIX' as const,
      nome: 'PIX',
      descricao: 'Pagamento instantâneo e seguro',
      beneficios: ['Aprovação imediata', 'Sem taxas', 'Mais seguro'],
      icon: <Smartphone className="w-8 h-8 text-green-500" />,
      cor: 'green'
    },
    {
      id: 'CREDIT_CARD' as const,
      nome: 'Cartão de Crédito',
      descricao: 'Pague com seu cartão preferido',
      beneficios: ['Parcelamento disponível', 'Pontos no cartão', 'Aprovação rápida'],
      icon: <CreditCard className="w-8 h-8 text-blue-500" />,
      cor: 'blue'
    }
  ];

  const handleSelect = async (metodo: 'PIX' | 'CREDIT_CARD') => {
    setLoading(true);
    try {
      await onSelect(metodo);
    } finally {
      setLoading(false);
    }
  };

  const getColorClasses = (cor: string, isSelected: boolean) => {
    const baseClasses = 'border-2 transition-all duration-300 hover:shadow-lg cursor-pointer';
    
    if (isSelected) {
      switch (cor) {
        case 'green':
          return `${baseClasses} border-green-500 bg-green-50 ring-2 ring-green-200`;
        case 'blue':
          return `${baseClasses} border-blue-500 bg-blue-50 ring-2 ring-blue-200`;
        case 'orange':
          return `${baseClasses} border-orange-500 bg-orange-50 ring-2 ring-orange-200`;
        default:
          return `${baseClasses} border-gray-500 bg-gray-50 ring-2 ring-gray-200`;
      }
    }
    
    return `${baseClasses} border-gray-200 hover:border-gray-300 bg-white`;
  };

  return (
    <div className="max-w-4xl mx-auto p-6">
      <div className="text-center mb-8">
        <h2 className="text-2xl font-bold text-gray-900 mb-2">
          Método de Pagamento
        </h2>
        <p className="text-gray-600">
          Escolha como deseja pagar pelo plano <strong>{plano.nome}</strong>
        </p>
        <div className="mt-4 text-lg font-semibold text-gray-800">
          Valor: R$ {plano.preco.toFixed(2)}/mês
        </div>
      </div>

      <div className="grid md:grid-cols-3 gap-6 mb-8">
        {metodos.map((metodo) => (
          <div
            key={metodo.id}
            className={`rounded-lg p-6 ${getColorClasses(metodo.cor, metodoSelecionado === metodo.id)}`}
            onClick={() => handleSelect(metodo.id)}
          >
            <div className="text-center">
              <div className="flex justify-center mb-4">
                {metodo.icon}
              </div>
              
              <h3 className="text-lg font-semibold text-gray-900 mb-2">
                {metodo.nome}
              </h3>
              
              <p className="text-sm text-gray-600 mb-4">
                {metodo.descricao}
              </p>

              <div className="space-y-2 mb-4">
                {metodo.beneficios.map((beneficio, index) => (
                  <div key={index} className="flex items-center text-sm text-gray-700">
                    <Check className="w-4 h-4 text-green-500 mr-2 flex-shrink-0" />
                    <span>{beneficio}</span>
                  </div>
                ))}
              </div>

              {metodoSelecionado === metodo.id && (
                <div className="mt-4">
                  <div className="inline-flex items-center px-3 py-1 rounded-full text-sm font-medium bg-green-100 text-green-800">
                    <Check className="w-4 h-4 mr-1" />
                    Selecionado
                  </div>
                </div>
              )}
            </div>
          </div>
        ))}
      </div>

      {/* Informações de segurança */}
      <div className="bg-gray-50 rounded-lg p-6 mb-6">
        <h3 className="text-lg font-semibold text-gray-900 mb-3">
          🔒 Pagamento Seguro
        </h3>
        <div className="grid md:grid-cols-2 gap-4 text-sm text-gray-600">
          <div>
            <strong>Proteção de Dados:</strong> Seus dados são criptografados e protegidos
          </div>
          <div>
            <strong>Processamento:</strong> Pagamentos processados pelo Asaas
          </div>
          <div>
            <strong>Conformidade:</strong> Certificado PCI-DSS
          </div>
          <div>
            <strong>Suporte:</strong> Atendimento 24/7 para dúvidas
          </div>
        </div>
      </div>

      {/* Botões */}
      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="px-6 py-3 border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 transition-colors"
        >
          Voltar
        </button>
        
        <button
          onClick={() => metodoSelecionado && handleSelect(metodoSelecionado)}
          disabled={!metodoSelecionado || loading}
          className="px-6 py-3 bg-blue-600 text-white rounded-lg hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
        >
          {loading ? 'Processando...' : 'Continuar'}
        </button>
      </div>
    </div>
  );
};

export default MetodoPagamentoForm;
